import * as React from 'react';
import { createContext, useContext, useId, useMemo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@ui/utils';
import { Label } from '@ui/Label';
import { Separator } from '@ui/Separator';

type FieldContextValue = {
  size: 'medium' | 'small';
  disabled: boolean;
  invalid: boolean;
  id: string;
};

const FieldContext = createContext<FieldContextValue>({
  size: 'medium',
  disabled: false,
  invalid: false,
  id: '',
});

function useFieldContext() {
  return useContext(FieldContext);
}

function FieldSet({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        'flex flex-col gap-4 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
        className
      )}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = 'legend',
  ...props
}: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        'mb-1.5 font-medium data-[variant=label]:text-sm data-[variant=legend]:text-base',
        className
      )}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        'group/field-group @container/field-group flex w-full flex-col gap-5 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4',
        className
      )}
      {...props}
    />
  );
}

const fieldVariants = cva('group/field flex w-full gap-2 data-[invalid=true]:text-destructive', {
  variants: {
    orientation: {
      vertical: 'flex-col *:w-full [&>.sr-only]:w-auto',
      horizontal:
        'flex-row items-center has-[>[data-slot=field-content]]:items-start *:data-[slot=field-label]:flex-auto has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
      responsive:
        'flex-col *:w-full @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:*:data-[slot=field-label]:flex-auto [&>.sr-only]:w-auto @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
});

interface FieldProps extends React.ComponentProps<'div'>, VariantProps<typeof fieldVariants> {
  size?: 'medium' | 'small';
  disabled?: boolean;
  invalid?: boolean;
}

/**
 * A compound field component for form inputs with label, description, error, and separator support.
 *
 * @see {@link https://www.figma.com/design/DMtZ2xoJhsy1gKZAJvRDVA/Sirona-Medical-Design-System?node-id=5672-45412&m=dev Figma}
 */
function Field({
  className,
  orientation = 'vertical',
  size = 'medium',
  disabled = false,
  invalid = false,
  ...props
}: FieldProps) {
  const generatedId = useId();

  const ctx = useMemo<FieldContextValue>(
    () => ({ size, disabled, invalid, id: generatedId }),
    [size, disabled, invalid, generatedId]
  );

  return (
    <FieldContext.Provider value={ctx}>
      <div
        role="group"
        data-slot="field"
        data-orientation={orientation}
        data-disabled={disabled || undefined}
        data-invalid={invalid || undefined}
        className={cn(fieldVariants({ orientation }), className)}
        {...props}
      />
    </FieldContext.Provider>
  );
}

function FieldContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-content"
      className={cn('group/field-content flex flex-1 flex-col gap-0.5 leading-snug', className)}
      {...props}
    />
  );
}

interface FieldLabelProps extends React.ComponentProps<typeof Label> {
  /** Show a red asterisk indicating the field is required */
  required?: boolean;
  /** Optional ReactNode rendered at the right end of the label row (e.g. a toggle) */
  toggle?: React.ReactNode;
}

function FieldLabel({ className, required, toggle, children, ...props }: FieldLabelProps) {
  const { id } = useFieldContext();

  return (
    <Label
      data-slot="field-label"
      htmlFor={id}
      className={cn(
        'flex h-24 w-full items-center justify-between pl-8 text-xs leading-16 text-field-text-placeholder-label',
        'group-data-[disabled=true]/field:opacity-50',
        className
      )}
      {...props}
    >
      <span className="flex items-center gap-px">
        {children}
        {required && <span className="text-text-status-error">*</span>}
      </span>
      {toggle}
    </Label>
  );
}

function FieldTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        'flex w-fit items-center gap-2 text-sm leading-snug font-medium group-data-[disabled=true]/field:opacity-50',
        className
      )}
      {...props}
    />
  );
}

const fieldControlVariants = cva(
  [
    'flex w-full items-center justify-between rounded-lg px-8 py-4',
    'bg-field-bg border border-field-border',
    'hover:border-field-border-hover',
    'focus-within:border-field-border-hover',
    'group-data-[invalid=true]/field:border-border-status-error',
    'group-data-[disabled=true]/field:border-transparent group-data-[disabled=true]/field:opacity-64',
    'transition-colors',
  ],
  {
    variants: {
      size: {
        medium: 'h-32',
        small: 'h-28',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  }
);

const ICON_CLASSES =
  'flex shrink-0 items-center justify-center size-16 text-field-icon group-data-[disabled=true]/field:text-field-icon-disabled';

const ICON_BUTTON_CLASSES = `${ICON_CLASSES} appearance-none bg-transparent border-none p-0 cursor-pointer`;

function FieldControlIcon({
  icon,
  onClick,
  'aria-label': ariaLabel,
}: {
  icon: React.ReactNode;
  onClick?: () => void;
  'aria-label'?: string;
}) {
  if (onClick) {
    return (
      <button
        type="button"
        className={ICON_BUTTON_CLASSES}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {icon}
      </button>
    );
  }
  return <span className={ICON_CLASSES}>{icon}</span>;
}

interface FieldControlProps extends React.ComponentProps<'div'> {
  /** Icon rendered at the left of the input area */
  iconLeft?: React.ReactNode;
  /** First icon rendered at the right of the input area */
  iconRight1?: React.ReactNode;
  /** Click handler for iconRight1 — renders as a button when provided */
  onClickIconRight1?: () => void;
  /** Accessible label for iconRight1 button */
  'aria-label-icon-right-1'?: string;
  /** Second icon rendered at the right of the input area */
  iconRight2?: React.ReactNode;
  /** Click handler for iconRight2 — renders as a button when provided */
  onClickIconRight2?: () => void;
  /** Accessible label for iconRight2 button */
  'aria-label-icon-right-2'?: string;
}

function FieldControl({
  className,
  iconLeft,
  iconRight1,
  onClickIconRight1,
  'aria-label-icon-right-1': ariaLabelRight1,
  iconRight2,
  onClickIconRight2,
  'aria-label-icon-right-2': ariaLabelRight2,
  children,
  ...props
}: FieldControlProps) {
  const { size, id } = useFieldContext();

  return (
    <div
      data-slot="field-control"
      className={cn(fieldControlVariants({ size }), className)}
      {...props}
    >
      <div className="flex flex-1 items-center gap-4 min-w-0">
        {iconLeft && <span className={ICON_CLASSES}>{iconLeft}</span>}
        <div
          className={cn(
            'flex flex-1 min-w-0 text-sm leading-18 text-field-text',
            '[&_input]:w-full [&_input]:bg-transparent [&_input]:outline-none [&_input]:border-none [&_input]:p-0 [&_input]:text-field-text [&_input]:placeholder:text-field-text-placeholder-label',
            '[&_input]:disabled:text-field-text-disabled [&_input]:disabled:cursor-not-allowed'
          )}
          data-field-id={id}
        >
          {children}
        </div>
      </div>
      {(iconRight1 || iconRight2) && (
        <div className="flex shrink-0 items-center gap-4">
          {iconRight1 && (
            <FieldControlIcon
              icon={iconRight1}
              onClick={onClickIconRight1}
              aria-label={ariaLabelRight1}
            />
          )}
          {iconRight2 && (
            <FieldControlIcon
              icon={iconRight2}
              onClick={onClickIconRight2}
              aria-label={ariaLabelRight2}
            />
          )}
        </div>
      )}
    </div>
  );
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        'pl-8 pt-px text-xs leading-16 text-field-text-placeholder-label',
        'group-data-[invalid=true]/field:hidden',
        className
      )}
      {...props}
    />
  );
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn(
        'relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2',
        className
      )}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="relative mx-auto block w-fit bg-background px-2 text-muted-foreground"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  );
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<'div'> & {
  errors?: Array<{ message?: string } | undefined>;
}) {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const definedErrors = errors.filter((error): error is { message: string } => !!error?.message);
    const uniqueErrors = [
      ...new Map(definedErrors.map((error) => [error.message, error])).values(),
    ];

    if (uniqueErrors.length === 1) {
      return uniqueErrors[0].message;
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map((error, index) => (
          <li key={index}>{error.message}</li>
        ))}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn('pl-8 pt-px text-xs leading-16 text-text-status-error', className)}
      {...props}
    >
      {content}
    </div>
  );
}

/* eslint-disable react-refresh/only-export-components */
export {
  Field,
  fieldVariants,
  FieldControl,
  fieldControlVariants,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
};
/* eslint-enable react-refresh/only-export-components */

export type { FieldProps, FieldLabelProps, FieldControlProps };
